import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Divider
} from '@mui/material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { getScoreColor, formatNumber } from '../utils/scoreColors';

const YEAR_KEYS = [
  'montant_annee_n',
  'montant_annee_n1',
  'montant_annee_n2',
  'montant_annee_n3',
  'montant_annee_n4',
  'montant_annee_n5'
];

const REALIZED_KEYS = [
  'montant_realise_annee_n',
  'montant_realise_annee_n1',
  'montant_realise_annee_n2',
  'montant_realise_annee_n3',
  'montant_realise_annee_n4',
  'montant_realise_annee_n5'
];

function InvestmentBudget({ budgets, setBudgets, risks, setRisks, buildings = [] }) {
  const { t } = useTranslation();
  const [detailDialog, setDetailDialog] = useState({ open: false, index: null });
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Synchroniser les budgets d'investissement avec les risques par risk_analysis_id
    const budgetByRiskId = {};
    budgets.forEach(b => {
      if (b.risk_analysis_id) {
        budgetByRiskId[b.risk_analysis_id] = b;
      }
    });

    const newBudgets = risks.map((risk) => {
      const existing = risk.id ? budgetByRiskId[risk.id] : null;
      if (existing) {
        return {
          ...existing,
          batiment_id: risk.batiment_id ?? existing.batiment_id ?? null,
          thematique: risk.thematique,
          thematique_autre: risk.thematique_autre,
          item: risk.item,
          item_autre: risk.item_autre,
          urgence: risk.urgence,
          securite: risk.securite,
          reglementaire: risk.reglementaire,
          financier: risk.financier,
          ponderation_geo: risk.ponderation_geo,
          niveau_gravite: risk.niveau_gravite
        };
      }
      return {
        risk_analysis_id: risk.id || null,
        batiment_id: risk.batiment_id ?? null,
        annee_reference: currentYear,
        thematique: risk.thematique,
        thematique_autre: risk.thematique_autre,
        item: risk.item,
        item_autre: risk.item_autre,
        urgence: risk.urgence,
        securite: risk.securite,
        reglementaire: risk.reglementaire,
        financier: risk.financier,
        ponderation_geo: risk.ponderation_geo,
        niveau_gravite: risk.niveau_gravite,
        montant_investissement: 0,
        ajustement_pourcentage: 0,
        origine_chiffrage: '',
        montant_annee_n: 0,
        montant_annee_n1: 0,
        montant_annee_n2: 0,
        montant_annee_n3: 0,
        montant_annee_n4: 0,
        montant_annee_n5: 0,
        montant_realise_annee_n: 0,
        montant_realise_annee_n1: 0,
        montant_realise_annee_n2: 0,
        montant_realise_annee_n3: 0,
        montant_realise_annee_n4: 0,
        montant_realise_annee_n5: 0
      };
    });

    setBudgets(newBudgets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risks]);

  const calculateScore = (budget) => {
    const urgence = budget.urgence ? 1 : 0;
    const securite = budget.securite ? 1 : 0;
    const reglementaire = budget.reglementaire ? 1 : 0;
    const financier = budget.financier ? 1 : 0;
    const ponderation = parseFloat(budget.ponderation_geo || 100) / 100;

    return Math.round((urgence + securite + reglementaire + financier) * ponderation * 100) / 100;
  };

  const calculateAdjustedAmount = (budget) => {
    const montant = parseFloat(budget.montant_investissement || 0);
    const ajustement = parseFloat(budget.ajustement_pourcentage || 0);
    return Math.round(montant * (1 + ajustement / 100));
  };

  const calculateYearsTotal = (budget) => {
    return YEAR_KEYS.reduce((sum, k) => sum + parseFloat(budget[k] || 0), 0);
  };

  const calculateRealizedTotal = (budget) => {
    return REALIZED_KEYS.reduce((sum, k) => sum + parseFloat(budget[k] || 0), 0);
  };

  const checkBudgetAllocation = (budget) => {
    const adjustedAmount = calculateAdjustedAmount(budget);
    const yearsTotal = calculateYearsTotal(budget);
    return Math.round(adjustedAmount) === Math.round(yearsTotal);
  };

  const updateBudget = (index, field, value) => {
    const newBudgets = [...budgets];
    newBudgets[index] = { ...newBudgets[index], [field]: value };
    setBudgets(newBudgets);

    if (field === 'niveau_gravite' && risks && setRisks) {
      const newRisks = [...risks];
      if (newRisks[index]) {
        newRisks[index] = { ...newRisks[index], niveau_gravite: value };
        setRisks(newRisks);
      }
    }
  };

  const calculateTotal = () => {
    return budgets.reduce((sum, budget) => sum + parseFloat(calculateAdjustedAmount(budget)), 0);
  };

  const calculateRealizedGrandTotal = () => {
    return budgets.reduce((sum, budget) => sum + calculateRealizedTotal(budget), 0);
  };

  const getThematiqueLabel = (budget) => {
    if (budget.thematique === 'autre') {
      return budget.thematique_autre;
    }
    return t(`thematiques.${budget.thematique}`, budget.thematique);
  };

  const getItemLabel = (budget) => {
    let items = [];
    if (Array.isArray(budget.item)) {
      items = budget.item;
    } else if (typeof budget.item === 'string') {
      try {
        const parsed = JSON.parse(budget.item);
        items = Array.isArray(parsed) ? parsed : [budget.item];
      } catch {
        items = budget.item ? [budget.item] : [];
      }
    }

    const labels = items.map(item => {
      if (item === 'autre' && budget.item_autre) {
        return budget.item_autre;
      }
      return t(`riskItems.${item}`, item);
    });

    return labels.join(', ');
  };

  const getBuildingLabel = (budget) => {
    if (!budget.batiment_id) return t('entireSite');
    const b = buildings.find(x => x.id === budget.batiment_id);
    if (!b) return t('entireSite');
    return `${t('building')} ${b.numero_batiment}`;
  };

  const getReferenceYear = (budget) => {
    return parseInt(budget.annee_reference, 10) || currentYear;
  };

  const openDetailDialog = (index) => setDetailDialog({ open: true, index });
  const closeDetailDialog = () => setDetailDialog({ open: false, index: null });

  const detailBudget = detailDialog.index !== null ? budgets[detailDialog.index] : null;
  const detailRisk = detailDialog.index !== null && risks ? risks[detailDialog.index] : null;
  const isDetailValidated = detailRisk && detailRisk.validation_status === 'valide';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('investmentBudgetTab')}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('theme')}</TableCell>
              <TableCell>{t('item')}</TableCell>
              <TableCell>{t('scope')}</TableCell>
              <TableCell align="center">{t('score')}</TableCell>
              <TableCell align="center">{t('status')}</TableCell>
              <TableCell>{t('severityLevel')}</TableCell>
              <TableCell align="right">{t('investmentAmount')}</TableCell>
              <TableCell align="right">{t('adjustment')}</TableCell>
              <TableCell align="right" sx={{ minWidth: 120 }}>{t('adjustedAmount')}</TableCell>
              <TableCell>{t('quotationOrigin')}</TableCell>
              <TableCell align="center">{t('budgetAllocationControl')}</TableCell>
              <TableCell align="right">{t('totalRealized')}</TableCell>
              <TableCell align="center">{t('annualDetail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgets.map((budget, index) => {
              const isValidated = risks[index] && risks[index].validation_status === 'valide';
              const realizedTotal = calculateRealizedTotal(budget);
              return (
              <TableRow key={index}>
                <TableCell>{getThematiqueLabel(budget)}</TableCell>
                <TableCell>{getItemLabel(budget)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getBuildingLabel(budget)}
                    variant={budget.batiment_id ? 'filled' : 'outlined'}
                    color={budget.batiment_id ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: getScoreColor(calculateScore(budget)),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}
                  >
                    {calculateScore(budget)}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {risks[index] && risks[index].validation_status && (
                    <Chip
                      label={t(`validationStatus.${risks[index].validation_status}`)}
                      color={
                        risks[index].validation_status === 'valide' ? 'success' :
                        risks[index].validation_status === 'rejete' ? 'error' : 'warning'
                      }
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    value={budget.niveau_gravite}
                    onChange={(e) => updateBudget(index, 'niveau_gravite', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    disabled={isValidated}
                  >
                    <MenuItem value="critique">{t('critical')}</MenuItem>
                    <MenuItem value="important">{t('important')}</MenuItem>
                    <MenuItem value="modere">{t('moderate')}</MenuItem>
                    <MenuItem value="faible">{t('low')}</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    value={budget.montant_investissement}
                    onChange={(e) => updateBudget(index, 'montant_investissement', e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    size="small"
                    sx={{ width: 100 }}
                    disabled={isValidated}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    value={budget.ajustement_pourcentage}
                    onChange={(e) => updateBudget(index, 'ajustement_pourcentage', e.target.value)}
                    inputProps={{ step: 1 }}
                    size="small"
                    sx={{ width: 80 }}
                    disabled={isValidated}
                  />
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  <strong>{formatNumber(calculateAdjustedAmount(budget))}</strong>
                </TableCell>
                <TableCell>
                  <TextField
                    value={budget.origine_chiffrage}
                    onChange={(e) => updateBudget(index, 'origine_chiffrage', e.target.value)}
                    size="small"
                    sx={{ width: 150 }}
                    disabled={isValidated}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={checkBudgetAllocation(budget) ? 'OK' : 'KO'}
                    size="small"
                    sx={{
                      bgcolor: checkBudgetAllocation(budget) ? '#2e7d32' : '#d32f2f',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <strong>{formatNumber(realizedTotal)}</strong>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={`${t('annualBreakdown')} (${getReferenceYear(budget)} → ${getReferenceYear(budget) + 5})`}>
                    <IconButton onClick={() => openDetailDialog(index)} size="small" color="primary">
                      <CalendarIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}
            {budgets.length > 0 && (
              <TableRow>
                <TableCell colSpan={7} align="right">
                  <Typography variant="h6">Total:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6">{formatNumber(calculateTotal())}</Typography>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
                <TableCell align="right">
                  <Typography variant="h6">{formatNumber(calculateRealizedGrandTotal())}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {budgets.length === 0 && (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          Ajoutez des risques dans la section "Analyse des risques" pour créer automatiquement les lignes de budget d'investissement.
        </Typography>
      )}

      <Dialog open={detailDialog.open} onClose={closeDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('annualBreakdown')}
          {detailBudget && (
            <Typography variant="body2" color="text.secondary">
              {getThematiqueLabel(detailBudget)} — {getItemLabel(detailBudget)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailBudget && (
            <Box>
              <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('scope')}</Typography>
                  <Typography variant="body2">{getBuildingLabel(detailBudget)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('referenceYear')}</Typography>
                  <Typography variant="body2">{getReferenceYear(detailBudget)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('adjustedAmount')}</Typography>
                  <Typography variant="body2"><strong>{formatNumber(calculateAdjustedAmount(detailBudget))}</strong></Typography>
                </Box>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('year')}</TableCell>
                    <TableCell align="right">{t('forecast')}</TableCell>
                    <TableCell align="right">{t('realized')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {YEAR_KEYS.map((forecastKey, i) => {
                    const realizedKey = REALIZED_KEYS[i];
                    const year = getReferenceYear(detailBudget) + i;
                    return (
                      <TableRow key={forecastKey}>
                        <TableCell><strong>{year}</strong></TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={detailBudget[forecastKey] ?? 0}
                            onChange={(e) => updateBudget(detailDialog.index, forecastKey, e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            size="small"
                            sx={{ width: 140 }}
                            disabled={isDetailValidated}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={detailBudget[realizedKey] ?? 0}
                            onChange={(e) => updateBudget(detailDialog.index, realizedKey, e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            size="small"
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align="right">
                      <strong>{formatNumber(calculateYearsTotal(detailBudget))}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatNumber(calculateRealizedTotal(detailBudget))}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">
                  {t('budgetAllocationControl')} :
                </Typography>
                <Chip
                  label={checkBudgetAllocation(detailBudget) ? 'OK' : 'KO'}
                  size="small"
                  sx={{
                    bgcolor: checkBudgetAllocation(detailBudget) ? '#2e7d32' : '#d32f2f',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>{t('close', 'Fermer')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvestmentBudget;
